/**
 * Pure helpers for the Commandes view (design §1.4, contract §2
 * `GET waiter-mode/sessions/<uuid>/`). Grouping, the merge key and the
 * locked-line predicate are all pure so F4's `TicketCommandes` can unit-test
 * them without mounting a component or a store.
 */

import type { DineInCourse } from '@/types';

export interface CommandesOrderItemOption {
  name?: string;
  label?: string;
  qty?: number;
}

/** The subset of `WaiterOrderItem` (waiterApi.ts) these helpers read. */
export interface CommandesGroupable {
  category: string;
  productUuid: string;
  options: CommandesOrderItemOption[];
  comment: string;
  roundNumber: number;
}

/**
 * W-T-94 — one bucket per category, in the given `categoryOrder` (the menu's
 * category order). A category not in `categoryOrder` (deleted/renamed since
 * the order was placed) is appended, in first-seen order, rather than
 * silently dropping its items. Categories with zero items are omitted.
 */
export function groupByCategory<T extends { category: string }>(
  items: T[],
  categoryOrder: string[],
): { category: string; items: T[] }[] {
  const byCategory = new Map<string, T[]>();
  for (const it of items) {
    const bucket = byCategory.get(it.category);
    if (bucket) bucket.push(it);
    else byCategory.set(it.category, [it]);
  }
  const ordered = [
    ...categoryOrder.filter((c) => byCategory.has(c)),
    ...[...byCategory.keys()].filter((c) => !categoryOrder.includes(c)),
  ];
  return ordered.map((category) => ({ category, items: byCategory.get(category)! }));
}

/**
 * W-T-95 — one Commandes line ⇔ one `OrderItem`, but two `OrderItem`s that
 * are otherwise identical only display as ONE merged line when they also
 * share a round: `(produit, options, commentaire, tournée)` per spec §1.7.
 * Option order must not affect the key (server option ordering isn't
 * guaranteed stable).
 */
export function commandesLineMergeKey(item: CommandesGroupable): string {
  const opts = [...item.options]
    .map((o) => ({ key: o.name ?? o.label ?? '', qty: o.qty ?? 1 }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return JSON.stringify({
    productUuid: item.productUuid,
    opts,
    comment: item.comment ?? '',
    roundNumber: item.roundNumber,
  });
}

export interface RoundValidationState {
  /** `online_status` of the ORDER the line belongs to. */
  onlineStatus: string;
  roundNumber: number | null;
  /** `last_validated_round_number` of that order — the SC-356 boundary. */
  lastValidatedRoundNumber: number | null;
}

/**
 * SC-787 — a table merges every round into ONE anchor order, and a new
 * unvalidated round puts that whole anchor back to `pending_validation`
 * (backend SC-336). So `onlineStatus` alone says THAT the table has something
 * to confirm, never WHICH round: reading it per line painted « En attente »
 * on the rounds the waiter had just confirmed, raising the fear of a second
 * print of dishes already sent.
 *
 * The boundary is `last_validated_round_number` (backend SC-356) — the exact
 * same line Valider locks in and Refuser drops. Rounds at or below it are
 * confirmed; only what sits above is waiting. `null` means no round has been
 * validated yet, so the anchor really is pending in full — and a line with no
 * round at all (legacy row) is pending with it rather than silently passing
 * for confirmed.
 */
export function isRoundPendingValidation(state: RoundValidationState): boolean {
  if (state.onlineStatus !== 'pending_validation') return false;
  if (state.roundNumber == null) return true;
  return state.roundNumber > (state.lastValidatedRoundNumber ?? 0);
}

export interface CommandesLockState {
  onlineStatus: string;
  paymentStatus: string;
  caisseHandoffStatus: string;
  isCartOption: boolean;
}

/**
 * W-T-96 — mirrors the design §1.4 decrement refusal table (backend
 * `ITEM_LOCKED` 409 reasons) so the "−" can be disabled/confirmed client-side
 * without a round-trip. The server call remains authoritative; this predicate
 * is UI-only.
 */
export function isCommandesLineLocked(state: CommandesLockState): boolean {
  if (state.isCartOption) return true; // 400 NOT_A_DISH — a cart-option line, not a dish
  if (state.paymentStatus === 'paid' || state.paymentStatus === 'partial') return true;
  if (state.onlineStatus === 'handed_over' || state.onlineStatus === 'cancelled') return true;
  if (state.onlineStatus === 'pending_validation') return true; // → valider/refuser d'abord
  if (state.caisseHandoffStatus === 'requested' || state.caisseHandoffStatus === 'imported') return true;
  return false;
}

/**
 * SC-902 — design §1.2: 1-based position of the service whose
 * `categoryUuids` contains `categoryId`; `null` when the category belongs to
 * no configured service (unclassified, or the option is off / `courses` is
 * empty).
 */
export function courseIndexForCategory(courses: DineInCourse[], categoryId: string): number | null {
  for (let i = 0; i < courses.length; i += 1) {
    if (courses[i].categoryUuids.includes(categoryId)) return i + 1;
  }
  return null;
}

export interface CourseGroup<T> {
  course: number;
  label: string;
  lines: T[];
}

/**
 * SC-902 — design §1.2 resolution rules, shared by the confirm sheet (T6)
 * and the counterpart backend split: a line's service = the position of the
 * course whose `categoryUuids` contains its product's category; unclassified
 * lines (unknown product, or a category assigned to no service) join the
 * FIRST present service rather than being dropped or spun off on their own
 * (display grouping only — the send plan keeps them apart, SC-1614:
 * `unclassifiedLines` + `planSendByCourse({ unclassified })`).
 * Present services = the sorted distinct service indexes actually found in
 * the cart. Zero present services (the option is on but nothing in the cart
 * is classified yet) folds into a single group on service 1, carrying every
 * line untouched — the natural "nothing to split" case. Every group keeps
 * the cart's original line order.
 */
export function splitCartByCourse<T extends { itemId: string }>(
  lines: T[],
  courses: DineInCourse[],
  categoryIdByProduct: Map<string, string>,
): CourseGroup<T>[] {
  const indexed = lines.map((line) => {
    const categoryId = categoryIdByProduct.get(line.itemId);
    const course = categoryId != null ? courseIndexForCategory(courses, categoryId) : null;
    return { line, course };
  });
  const presentCourses = [...new Set(indexed.map((e) => e.course).filter((c): c is number => c != null))].sort(
    (a, b) => a - b,
  );
  if (presentCourses.length === 0) {
    return [{ course: 1, label: courses[0]?.label ?? '', lines }];
  }
  const firstPresent = presentCourses[0];
  const linesByCourse = new Map<number, T[]>();
  for (const { line, course } of indexed) {
    const bucket = linesByCourse.get(course ?? firstPresent);
    if (bucket) bucket.push(line);
    else linesByCourse.set(course ?? firstPresent, [line]);
  }
  return presentCourses.map((course) => ({
    course,
    label: courses[course - 1]?.label ?? '',
    lines: linesByCourse.get(course) ?? [],
  }));
}

export interface HeldRoundOrder {
  heldRounds: number[];
  items: { roundNumber: number; isCartOption: boolean }[];
  /** Reviewer finding F2 — the session detail still returns a cancelled
   *  order with `held_rounds` populated (a whole-order cancel doesn't clear
   *  it), so a cancelled order's rounds must never be offered as a live
   *  `join` target: the sheet would plan against a dead round, get a 400,
   *  invalidate, and replan the exact same dead join. Optional so the
   *  existing fixtures (no field at all) keep behaving as "not cancelled". */
  onlineStatus?: string;
}

/**
 * SC-902 — design §1.6/§1.7: a held round the "−" has emptied of every real
 * dish (only cart-option lines left, or nothing at all) has nothing left to
 * fire and must not show a « Lancer » row. Returned ascending.
 *
 * F2 — a cancelled order's held rounds are never live, regardless of what
 * `items`/`heldRounds` still say (see `HeldRoundOrder.onlineStatus`).
 */
export function liveHeldRounds(order: HeldRoundOrder): number[] {
  if (order.onlineStatus === 'cancelled') return [];
  const roundsWithDishes = new Set(
    order.items.filter((item) => !item.isCartOption).map((item) => item.roundNumber),
  );
  return order.heldRounds.filter((round) => roundsWithDishes.has(round)).sort((a, b) => a - b);
}

export interface HeldRoundLabelOrder extends HeldRoundOrder {
  /** SC-902 — design §1.3: `order.round_service_labels`, the label frozen
   *  when that round was created. `{}` on an older server / a round created
   *  before this feature existed. */
  roundServiceLabels: Record<string, string>;
}

export interface HeldRoundInfo {
  round: number;
  /** The frozen label — may not match any course still configured today
   *  (a service renamed since, or the option turned off and back on with a
   *  different plan). Empty string if the round never got one. */
  label: string;
  /** 1-based position of `label` in the CURRENT `courses`, or `null` when
   *  no course carries that label any more — comparisons against a course
   *  index must treat that round as neither earlier nor later than anything. */
  courseIndex: number | null;
}

/**
 * SC-902 — design §1.10 rule A: what the confirm sheet needs, per live held
 * round of the session, to plan a mid-meal addition against it — reused
 * across every order of the session detail (a table merges its rounds into
 * one anchor in practice, but nothing here assumes exactly one).
 */
export function heldRoundsOf(orders: HeldRoundLabelOrder[], courses: DineInCourse[]): HeldRoundInfo[] {
  const infos: HeldRoundInfo[] = [];
  for (const order of orders) {
    for (const round of liveHeldRounds(order)) {
      const label = order.roundServiceLabels[String(round)] ?? '';
      const position = courses.findIndex((c) => c.label === label);
      infos.push({ round, label, courseIndex: position === -1 ? null : position + 1 });
    }
  }
  return infos;
}

export interface CoursePlanEntry<T> {
  /** `now` = goes out with this round, unheld ; `hold` = becomes a NEW held
   *  round (`course` on the wire) ; `join` = rejoins an EXISTING held round
   *  of the anchor (`held_round` on the wire, carries `round`). */
  kind: 'now' | 'hold' | 'join';
  /** The service's own 1-based position (from `splitCartByCourse`) — display
   *  only; NOT the wire `course` number, which the caller renumbers
   *  sequentially over the `now`/`hold` entries only (§1.3: a request's
   *  `course` values are a pure ordering key, not a service id). */
  course: number;
  label: string;
  lines: T[];
  /** Set only when `kind === 'join'` — the held round number to attach to. */
  round?: number;
}

/**
 * SC-902 — design §1.10 rule A. With no live held round, this is exactly
 * T2's original split: the first service goes now, every other becomes a
 * newly held round. With held rounds already in flight, each service group
 * is classified against them:
 *   - same label as a held round (§1.1: renaming a service later never
 *     rewrites a frozen label, so equality here means "genuinely the same
 *     service instance") → `join` that round ;
 *   - otherwise, a position AFTER the lowest held course → a NEW held round
 *     (`hold`) — the golden rule "nothing overtakes a held service" ;
 *   - otherwise (before every held course, or no held course has a known
 *     position any more) → `now`, the safe default that never leaves food
 *     silently waiting.
 *
 * F5 — the server only ever fires ONE course per request (the lowest); it
 * never accepts several unheld `course` lines at once. Groups are walked in
 * ascending course order (see `splitCartByCourse`), so once the first `now`
 * candidate is found, every LATER one classified `now` by the rules above
 * is demoted to `hold` instead — a new held round, not a second fire. This
 * mirrors the no-held-round branch above, which already lets only `index
 * === 0` be `now`.
 */
export function planSendByCourse<T>({
  groups,
  heldRounds,
  unclassified = [],
}: {
  groups: CourseGroup<T>[];
  heldRounds: HeldRoundInfo[];
  /** SC-1614 — lines no service claims (a drink, a formule). They never
   *  wait: they ride with the `now` entry, or form their own `now` entry
   *  (`course` 0, empty `label`) when every service is held or joined. */
  unclassified?: T[];
}): CoursePlanEntry<T>[] {
  const plan = planServices(groups, heldRounds);
  if (unclassified.length === 0) return plan;
  const now = plan.find((entry) => entry.kind === 'now');
  if (now) {
    now.lines = [...now.lines, ...unclassified];
    return plan;
  }
  return [{ kind: 'now', course: 0, label: '', lines: unclassified }, ...plan];
}

/**
 * SC-1614 — the cart's lines that no service claims (unknown product, or a
 * category assigned to no service): `planSendByCourse`'s `unclassified`.
 */
export function unclassifiedLines<T extends { itemId: string }>(
  lines: T[],
  courses: DineInCourse[],
  categoryIdByProduct: Map<string, string>,
): T[] {
  return lines.filter((line) => {
    const categoryId = categoryIdByProduct.get(line.itemId);
    return categoryId == null || courseIndexForCategory(courses, categoryId) == null;
  });
}

function planServices<T>(
  groups: CourseGroup<T>[],
  heldRounds: HeldRoundInfo[],
): CoursePlanEntry<T>[] {
  if (heldRounds.length === 0) {
    return groups.map((group, index) => ({
      kind: index === 0 ? 'now' : 'hold',
      course: group.course,
      label: group.label,
      lines: group.lines,
    }));
  }
  const knownHeldCourses = heldRounds
    .map((h) => h.courseIndex)
    .filter((c): c is number => c != null);
  const lowestHeldCourse = knownHeldCourses.length > 0 ? Math.min(...knownHeldCourses) : null;
  let sawNow = false;
  return groups.map((group) => {
    const rejoined = heldRounds.find((h) => h.label === group.label);
    if (rejoined) {
      return { kind: 'join', course: group.course, label: group.label, lines: group.lines, round: rejoined.round };
    }
    if (lowestHeldCourse != null && group.course > lowestHeldCourse) {
      return { kind: 'hold', course: group.course, label: group.label, lines: group.lines };
    }
    if (sawNow) {
      return { kind: 'hold', course: group.course, label: group.label, lines: group.lines };
    }
    sawNow = true;
    return { kind: 'now', course: group.course, label: group.label, lines: group.lines };
  });
}

/**
 * SC-902 — design §1.7/§1.10 rule A: no choice to offer when every entry of
 * the plan simply goes out now — the single-service case with no held
 * rounds, but ALSO a lone service that happens to match a held round's label
 * still counts as non-trivial (it must show as a `join`, per rule A).
 */
export function planIsTrivial<T>(plan: CoursePlanEntry<T>[]): boolean {
  return plan.every((entry) => entry.kind === 'now');
}
